export interface LineItem {
  id: string
  description: string
  currency: string
  quantity: number
  unitAmount: number
  lineType:
    | "product_none_shippable"
    | "sales_tax"
    | "subtotal"
    | "total"
    | "product_shippable"
  taxRate?: number
  priceId?: string
  productId?: string
}

interface Invoice {
  id: string
  created: {
    formatted: string
  }
  periodStart: {
    formatted: string
  }
  periodEnd: {
    formatted: string
  }
  status: string
  total: number
}

export type SubscriptionProvider = "google" | "apple" | "stripe" | "unknown"

export interface SubscriptionData {
  id: string
  paymentIntentId: string
  status: string // "active", "inactive", "trialing"
  provider: SubscriptionProvider
  currentPeriodEnd: {
    formatted: string
  }
  paidUntilDate: {
    formatted: string
  }
  subscriptionId: string
  cancelAtPeriodEnd: boolean
  lineItems: LineItem[]
  created: {
    formatted: string
  }
  confirmationCode: string
  invoices: Invoice[]
  providerPaymentMethodId: string
  trialDaysOnHeadsetConnect?: number
  headsetConnectedTrialActivated?: boolean
  discountAmount?: number
  couponCode?: string
  level?: "none" | "member" | "pro"
  price: number
  currency: string
}

export class Subscription {
  id: string
  paymentIntentId: string
  status: string // "active", "inactive", "trialing"
  provider: SubscriptionProvider
  currentPeriodEnd: {
    formatted: string
  }
  paidUntilDate: {
    formatted: string
  }
  subscriptionId: string
  cancelAtPeriodEnd: boolean
  lineItems: LineItem[]
  created: {
    formatted: string
  }
  confirmationCode: string
  invoices: Invoice[]
  providerPaymentMethodId: string
  trialDaysOnHeadsetConnect?: number
  headsetConnectedTrialActivated?: boolean
  discountAmount?: number
  couponCode?: string
  level?: "none" | "member" | "pro"

  price: number
  currency: string

  constructor(props: SubscriptionData) {
    this.id = props.id
    this.status = props.status
    this.provider = props.provider
    this.paymentIntentId = props.paymentIntentId
    this.currentPeriodEnd = props.currentPeriodEnd
    this.paidUntilDate = props.paidUntilDate
    this.subscriptionId = props.subscriptionId
    this.cancelAtPeriodEnd = props.cancelAtPeriodEnd
    this.lineItems = props.lineItems
    this.created = props.created
    this.confirmationCode = props.confirmationCode
    this.invoices = props.invoices
    this.providerPaymentMethodId = props.providerPaymentMethodId
    this.trialDaysOnHeadsetConnect = props.trialDaysOnHeadsetConnect
    this.discountAmount = props.discountAmount
    this.couponCode = props.couponCode
    this.level = props.level
    this.price = props.price
    this.currency = props.currency
  }

  isProfesssional() {
    return this.level === "pro"
  }

  isPersonal() {
    return this.level === "member"
  }

  formatStatus = (): string => {
    if (this.cancelAtPeriodEnd) {
      return "Canceled"
    }

    switch (this.status) {
      case "active":
        return "Active"
      case "incomplete":
        return "Incomplete"
      case "incomplete_expired":
        return "Incompelete / Expired"
      case "trialing":
        return "Free Trial Period"
      case "past_due":
        return "Past Due"
      case "unpaid":
        return "Payment Required"
      default:
        return this.status
    }
  }

  isStripe = () => this.provider === "stripe"
  isApple = () => this.provider === "apple"
  isGoogle = () => this.provider === "google"
  isActive = () => this.status === "active"

  isTrialPendingHeadsetRegistration(): boolean {
    return (
      !!this.trialDaysOnHeadsetConnect &&
      this.trialDaysOnHeadsetConnect > 0 &&
      !this.headsetConnectedTrialActivated
    )
  }
}
